import React, { useState } from "react";
import { Check } from "lucide-react";
import Modal from "./Modal";
import PrimaryButton from "../components/Buttons/PrimaryButton";
import LoadingIcons from "react-loading-icons";
import { createOrganization } from "../utils/Api";
import { useSendEmail } from "../hooks/useSendEmail";
import { organizationInvitationEmail } from "../emails/organizationInvitation";

interface CreateOrganizationModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onSuccess: () => void;
}

const CreateOrganizationModal: React.FC<CreateOrganizationModalProps> = ({
  isOpen,
  setIsOpen,
  onSuccess,
}) => {
  const [name, setName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [sentEmail, setSentEmail] = useState("");
  const { sendEmail } = useSendEmail();

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError("Organisation name is required");
      return;
    }
    if (!adminEmail.trim()) {
      setError("Admin email is required");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await createOrganization(name.trim(), adminEmail.trim());

      await sendEmail({
        to: adminEmail.trim(),
        template: organizationInvitationEmail({
          organizationName: response.data.organization_name,
          invitationUrl: response.data.invitation_url,
        }),
      });

      setSentEmail(adminEmail.trim());
      setName("");
      setAdminEmail("");
      setSuccess(true);
      onSuccess();
    } catch (err: any) {
      console.error("Failed to create organization:", err);
      setError(err.response?.data?.detail || "Failed to create organisation");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setName("");
      setAdminEmail("");
      setError(null);
      setSuccess(false);
      setSentEmail("");
      setIsOpen(false);
    }
  };

  if (success) {
    return (
      <Modal isOpen={isOpen} setIsOpen={handleClose}>
        <div className="text-center py-4">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <Check className="h-6 w-6 text-green-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Invitation sent
          </h3>
          <p className="text-sm text-gray-600 mb-6">
            An invitation has been sent to <strong>{sentEmail}</strong>
          </p>
          <button
            type="button"
            onClick={handleClose}
            className="inline-flex justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500"
          >
            Done
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} setIsOpen={handleClose}>
      <div className="sm:flex sm:items-start">
        <div className="mt-3 sm:mt-0 text-left w-full">
          <h3 className="text-lg font-semibold leading-6 text-gray-900 mb-4">
            Create organisation
          </h3>

          {error && (
            <div
              className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4"
              role="alert"
            >
              {error}
            </div>
          )}

          <div className="mb-4">
            <label
              htmlFor="org-name"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Organisation name
            </label>
            <input
              type="text"
              id="org-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter organisation name"
              disabled={loading}
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="admin-email"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Admin email
            </label>
            <input
              type="email"
              id="admin-email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="admin@example.com"
              disabled={loading}
            />
          </div>

          <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-2">
            <PrimaryButton
              onClick={handleSubmit}
              disabled={loading}
              className="w-full sm:w-auto"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <LoadingIcons.TailSpin stroke="#fff" width="16" height="16" />
                  Sending...
                </span>
              ) : (
                "Send invitation"
              )}
            </PrimaryButton>
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="mt-3 sm:mt-0 w-full sm:w-auto rounded-md bg-white px-4 py-2 font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CreateOrganizationModal;
